/*
  Sydney Hi-Fi theme: behaviour shared by every page. Loaded as a module from layout/theme.liquid.
  - x-drawer: menu, cart, search and dialogs (focus in, focus trap, Escape, focus back without scrolling)
  - header: height variable, transparent-to-black on the homepage, touch-friendly mega menus
  - opening hours: open now line, today's row and special hours notices in Sydney time (cached pages stay right)
  - x-rail: arrow buttons for sideways product rows
  - cart: add to cart without leaving the page, then refresh and open the cart drawer (Section Rendering API)
  - reveal: fades sections in as they scroll into view
  - tickers: run moving word tickers only while on screen
*/
const SHF = (window.SHF = window.SHF || {});
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ x-drawer */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

class XDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trap(e);
    });
  }
  get panel() {
    return this.querySelector('[role="dialog"]') || this;
  }
  open(opener) {
    if (this.hasAttribute('open')) return;
    this.opener = opener || document.activeElement;
    this.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    document.querySelectorAll(`[aria-controls="${this.id}"][aria-expanded]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const focusFirst = () => {
      const target = this.querySelector('[autofocus]') || this.panel;
      target.focus({ preventScroll: true });
    };
    focusFirst();
    // A visibility transition can leave the first frame hidden, so try again once the panel is showing.
    setTimeout(focusFirst, 60);
    this.dispatchEvent(new CustomEvent('drawer:open'));
  }
  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.documentElement.style.overflow = '';
    document.querySelectorAll(`[aria-controls="${this.id}"][aria-expanded]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('drawer:close'));
  }
  trap(e) {
    const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
customElements.define('x-drawer', XDrawer);

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (!drawer || typeof drawer.open !== 'function') return;
  e.preventDefault();
  document.querySelectorAll('x-drawer[open]').forEach((d) => d !== drawer && d.close());
  drawer.open(opener);
});
SHF.openDrawer = (id) => document.getElementById(id)?.open();

// Search: load predictive search the first time the overlay opens.
const searchOverlay = document.querySelector('[data-search-overlay]');
searchOverlay?.addEventListener('drawer:open', () => import(searchOverlay.dataset.searchSrc), { once: true });

/* ------------------------------------------------------------------ header */
const header = document.querySelector('[data-site-header]');
if (header) {
  const setHeight = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
  setHeight();
  new ResizeObserver(setHeight).observe(header);

  if (header.hasAttribute('data-overlay')) {
    const hero = document.querySelector('[data-hero]');
    const update = () => {
      const limit = hero ? hero.offsetHeight * 0.6 : 80;
      header.toggleAttribute('data-scrolled', window.scrollY > limit);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  // Touch screens: first tap on a mega menu opens it, a second tap follows the link.
  const items = header.querySelectorAll('[data-nav-item]');
  items.forEach((item) => {
    const trigger = item.querySelector('[data-nav-trigger]');
    trigger.addEventListener('click', (e) => {
      if (window.matchMedia('(hover: hover)').matches) return;
      if (!item.hasAttribute('data-open')) {
        e.preventDefault();
        items.forEach((other) => other !== item && other.removeAttribute('data-open'));
        item.setAttribute('data-open', '');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
    item.addEventListener('focusin', () => trigger.setAttribute('aria-expanded', 'true'));
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) trigger.setAttribute('aria-expanded', 'false');
    });
    item.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      item.removeAttribute('data-open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
      // Let keyboard users close a menu opened by focus-within: blur moves focus out and back to the trigger.
      item.classList.add('pointer-events-none');
      setTimeout(() => item.classList.remove('pointer-events-none'), 250);
    });
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-nav-item]')) items.forEach((i) => i.removeAttribute('data-open'));
  });
}

/* ------------------------------------------------------------------ opening hours */
// Hours come from snippets/store-hours.liquid (#StoreHours in the layout): week = Monday to Sunday, special = dated
// hours ("closed" or "10:00-13:00"). Worked out here in Sydney time (daylight saving included) whatever the
// visitor's time zone, so a page from Shopify's cache still shows the right status and special hours notices.
const SYDNEY = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
function hoursStatus(data, now = Date.now()) {
  const p = Object.fromEntries(SYDNEY.formatToParts(new Date(now)).map((x) => [x.type, x.value]));
  const minutes = (Number(p.hour) % 24) * 60 + Number(p.minute);
  // A calendar day i days from today (Sydney), as a plain UTC date so adding days never meets a clock change.
  const dayAt = (i) => new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day) + i));
  const iso = (d) => d.toISOString().slice(0, 10);
  const span = (d) => {
    const v = String(data.special?.[iso(d)] ?? data.week?.[(d.getUTCDay() + 6) % 7] ?? '').trim();
    const m = v.match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
    return m ? { open: m[1] * 60 + Number(m[2]), close: m[3] * 60 + Number(m[4]) } : null;
  };
  const fmt = (t) => {
    const h = Math.floor(t / 60);
    const mm = t % 60;
    return `${((h + 11) % 12) + 1}${mm ? `:${String(mm).padStart(2, '0')}` : ''}${h >= 12 ? 'pm' : 'am'}`;
  };
  const today = dayAt(0);
  const result = { open: false, text: 'Showroom closed now', today: iso(today), weekday: today.getUTCDay() };
  const s = span(today);
  if (s && minutes >= s.open && minutes < s.close) return { ...result, open: true, text: `Showroom open now until ${fmt(s.close)}` };
  for (let i = s && minutes < s.open ? 0 : 1; i <= 62; i++) {
    const d = dayAt(i);
    const next = span(d);
    if (!next) continue;
    const name = d.toLocaleDateString('en-AU', { timeZone: 'UTC', weekday: 'long', ...(i > 6 && { day: 'numeric', month: 'long' }) });
    const when = i === 0 ? 'today' : i === 1 ? 'tomorrow' : name;
    return { ...result, text: `Showroom closed now, opens ${when} at ${fmt(next.open)}` };
  }
  return result;
}
function openingStatus() {
  let data;
  try {
    data = JSON.parse(document.getElementById('StoreHours').textContent);
  } catch {
    return;
  }
  const status = hoursStatus(data);
  document.querySelectorAll('[data-open-status]').forEach((el) => (el.textContent = status.text));
  document.querySelectorAll('[data-open-dot]').forEach((el) => {
    el.classList.toggle('animate-ping', status.open);
    el.classList.toggle('bg-signal', status.open);
    el.classList.toggle('bg-smoke', !status.open);
  });
  document.querySelectorAll('[data-store-hours] [data-day]').forEach((row) => row.toggleAttribute('data-today', row.dataset.day === String(status.weekday)));
  // Special hours notices: shown from data-from to data-until (Sydney dates, inclusive).
  document.querySelectorAll('[data-special-hours]').forEach((el) => (el.hidden = !(status.today >= el.dataset.from && status.today <= el.dataset.until)));
}
openingStatus();
document.addEventListener('visibilitychange', () => document.hidden || openingStatus());

/* ------------------------------------------------------------------ x-rail */
class XRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;
    const controls = document.querySelector(`[data-rail-controls="${this.track.id}"]`);
    this.prev = controls?.querySelector('[data-rail-prev]');
    this.next = controls?.querySelector('[data-rail-next]');
    const step = (dir) => {
      const card = this.track.firstElementChild;
      const width = card ? card.getBoundingClientRect().width + 20 : 300;
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / width));
      this.track.scrollBy({ left: dir * width * perPage, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };
    this.prev?.addEventListener('click', () => step(-1));
    this.next?.addEventListener('click', () => step(1));
    const update = () => {
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4;
      if (this.next) this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
    };
    this.track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(this.track);
    update();
  }
}
customElements.define('x-rail', XRail);

/* ------------------------------------------------------------------ x-brand-filter
   Live filter for brand lists (Brands mega menu, phone menu, brands page). Items are [data-brand-item] with
   data-brand="Name"; groups ([data-brand-group]) hide when empty. Enter opens the first match, or searches the
   store when nothing matches. Accents, spaces and punctuation are ignored, and initials match too ("bw" or "B&W" finds
   Bowers & Wilkins). */
const brandKey = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
// "Bowers & Wilkins" -> "bw", so people can type the shorthand they use.
const brandInitials = (s) =>
  String(s || '')
    .normalize('NFD')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w && w !== 'and')
    .map((w) => w[0])
    .join('');
class XBrandFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-brand-input]');
    if (!this.input || this.items) return;
    this.items = [...this.querySelectorAll('[data-brand-item]')].map((el) => ({ el, key: brandKey(el.dataset.brand), initials: brandInitials(el.dataset.brand) }));
    this.groups = [...this.querySelectorAll('[data-brand-group]')];
    this.count = this.querySelector('[data-brand-count]');
    this.empty = this.querySelector('[data-brand-empty]');
    this.term = this.querySelector('[data-brand-term]');
    this.searchLink = this.querySelector('[data-brand-search-link]');
    this.input.addEventListener('input', () => this.apply());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.input.value) {
        e.stopPropagation();
        this.input.value = '';
        this.apply();
      }
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const first = this.items.find((i) => !i.el.hidden);
      const q = this.input.value.trim();
      if (first && q) first.el.querySelector('a')?.click();
      else if (q && this.searchLink) window.location.href = this.searchLink.href;
    });
  }
  apply() {
    const raw = this.input.value.trim();
    const q = brandKey(raw);
    let shown = 0;
    for (const item of this.items) {
      const match = !q || item.key.includes(q) || (q.length > 1 && item.initials.startsWith(q));
      item.el.hidden = !match;
      if (match) shown += 1;
    }
    for (const group of this.groups) group.hidden = !group.querySelector('[data-brand-item]:not([hidden])');
    const total = this.count?.dataset.total || this.items.length;
    if (this.count) this.count.textContent = q ? `${shown} of ${total} brands` : `${total} brands`;
    if (this.empty) this.empty.hidden = shown > 0;
    if (this.term) this.term.textContent = raw;
    if (this.searchLink) {
      const url = new URL(SHF.routes?.search || '/search', window.location.origin);
      if (raw) url.searchParams.set('q', raw);
      this.searchLink.href = url.pathname + url.search;
    }
  }
}
customElements.define('x-brand-filter', XBrandFilter);

/* ------------------------------------------------------------------ cart */
const money = (cents) => {
  const format = SHF.moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2).replace(/\.00$/, '');
  const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, withCommas);
};
SHF.money = money;

async function refreshCart(sectionsHtml) {
  const html = sectionsHtml?.['cart-drawer'] ?? (await fetch(`${window.location.pathname}?sections=cart-drawer`).then((r) => r.json()))['cart-drawer'];
  if (!html) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-cart-drawer-inner]');
  const current = document.querySelector('[data-cart-drawer-inner]');
  if (fresh && current) current.replaceWith(fresh);
  const count = doc.querySelector('[data-cart-drawer-inner]')?.dataset.count ?? fresh?.dataset.count;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.classList.toggle('hidden', !Number(count));
  });
}
SHF.refreshCart = refreshCart;

async function addToCart(form, button) {
  const data = new FormData(form);
  data.append('sections', 'cart-drawer');
  data.append('sections_url', window.location.pathname);
  button?.setAttribute('aria-busy', 'true');
  if (button) button.disabled = true;
  try {
    const res = await fetch(`${SHF.routes.cartAdd}.js`, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
    const json = await res.json();
    if (!res.ok) throw new Error(json.description || json.message || 'Could not add to cart');
    await refreshCart(json.sections);
    document.getElementById('CartDrawer')?.open(button);
    form.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: json }));
  } catch (err) {
    const msg = form.querySelector('[data-form-error]');
    if (msg) {
      msg.textContent = err.message;
      msg.hidden = false;
    } else {
      window.alert(err.message);
    }
  } finally {
    button?.removeAttribute('aria-busy');
    if (button) button.disabled = false;
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target.closest('form[data-quick-add], form[data-product-form]');
  if (!form || !document.getElementById('CartDrawer')) return;
  e.preventDefault();
  addToCart(form, e.submitter || form.querySelector('[type="submit"]'));
});

// Cart drawer: quantity and remove, through /cart/change.js
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-cart-change]');
  if (!btn) return;
  e.preventDefault();
  const line = Number(btn.dataset.line);
  const quantity = Number(btn.dataset.quantity);
  btn.setAttribute('aria-busy', 'true');
  const res = await fetch(`${SHF.routes.cartChange}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
  });
  const json = await res.json();
  await refreshCart(json.sections);
  if (document.body.classList.contains('template-cart')) window.location.reload();
});

/* ------------------------------------------------------------------ enquiry dialog */
const enquiry = document.querySelector('[data-enquiry-dialog]');
if (enquiry) {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-drawer-open="EnquiryDialog"]');
    if (!btn) return;
    const topic = btn.dataset.enquiryTopic || 'General enquiry';
    const product = btn.dataset.enquiryProduct || '';
    const set = (sel, value, prop = 'value') => {
      const el = enquiry.querySelector(sel);
      if (el) el[prop] = value;
    };
    set('[data-enquiry-topic-field]', topic);
    set('[data-enquiry-product-field]', product);
    set('[data-enquiry-page-field]', window.location.pathname);
    set('[data-enquiry-eyebrow]', topic, 'textContent');
    set('[data-enquiry-heading]', btn.dataset.enquiryHeading || (product ? `Ask about ${product}` : 'How can we help?'), 'textContent');
    const message = enquiry.querySelector('[data-enquiry-message]');
    if (message && btn.dataset.enquiryMessage && !message.value) message.value = btn.dataset.enquiryMessage;
  });
  const form = enquiry.querySelector('[data-enquiry-form]');
  form?.addEventListener('submit', () => {
    try {
      sessionStorage.setItem('shf:enquiry-sent', '1');
    } catch {}
  });
  let sent = false;
  try {
    sent = sessionStorage.getItem('shf:enquiry-sent') === '1';
    sessionStorage.removeItem('shf:enquiry-sent');
  } catch {}
  if (sent) {
    enquiry.open();
  }
}

/* ------------------------------------------------------------------ lead attribution */
// Every lead records where the visitor came from (Casey's rule: referrer, UTMs, gclid, fbclid on every lead).
// First touch is kept for 90 days; the latest touch is added too when it differs. Stored only in this browser.
(() => {
  const KEY = 'shf:attribution';
  const LEAD_KEY = 'shf:lead-pending';
  const params = new URLSearchParams(window.location.search);
  const fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];
  const touch = { landing: window.location.pathname + window.location.search, referrer: document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : '', at: new Date().toISOString() };
  fields.forEach((k) => params.get(k) && (touch[k] = params.get(k)));
  const hasSignal = touch.referrer || fields.some((k) => touch[k]);
  let saved = {};
  try {
    saved = JSON.parse(localStorage.getItem(KEY) || '{}');
  } catch {}
  const fresh = !saved.first || Date.now() - new Date(saved.first.at).getTime() > 90 * 864e5;
  if (fresh) saved.first = touch;
  if (hasSignal || !saved.last) saved.last = touch;
  try {
    localStorage.setItem(KEY, JSON.stringify(saved));
  } catch {}

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.querySelector('input[name="form_type"]')?.value !== 'contact') return;
    const add = (name, value) => {
      if (!value) return;
      let input = form.querySelector(`input[name="contact[${name}]"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `contact[${name}]`;
        form.append(input);
      }
      input.value = value;
    };
    const t = saved.first || {};
    const l = saved.last || {};
    add('Source', [t.utm_source, t.utm_medium, t.utm_campaign].filter(Boolean).join(' / ') || (t.gclid ? 'google / cpc' : t.fbclid ? 'facebook / paid' : t.referrer ? 'referral' : 'direct'));
    add('Referrer', t.referrer);
    add('Landing page', t.landing);
    add('First visit', t.at);
    fields.forEach((k) => add(k, t[k]));
    if (l.at && l.at !== t.at) add('Latest source', [l.utm_source, l.utm_medium, l.utm_campaign, l.gclid && 'gclid', l.fbclid && 'fbclid', l.referrer].filter(Boolean).join(' / ') || 'direct');
    add('Page sent from', window.location.pathname);

    // Remember the lead until Shopify confirms it (?contact_posted=true), then GTM gets one generate_lead
    const ids = { EnquiryForm: 'enquiry', ContactForm: 'contact-page', BuilderForm: 'cinema-builder' };
    const value = (name) => form.querySelector(`[name="contact[${name}]"]`)?.value || '';
    // Enhanced conversions: Google hashes these in the browser. Email lower case, phone in +61 form.
    const email = value('email').trim().toLowerCase();
    let phone = value('phone').replace(/[^\d+]/g, '');
    if (/^0\d{9}$/.test(phone)) phone = `+61${phone.slice(1)}`;
    else if (/^61\d{9}$/.test(phone)) phone = `+${phone}`;
    const user_data = {};
    if (email.includes('@')) user_data.email = email;
    if (/^\+\d{8,15}$/.test(phone)) user_data.phone_number = phone;
    try {
      sessionStorage.setItem(LEAD_KEY, JSON.stringify({
        form_id: ids[form.id] || form.id || 'contact',
        lead_topic: value('Topic'),
        product_name: value('Product'),
        form_page: window.location.pathname,
        user_data,
      }));
    } catch {}
  }, true);

  // One lead event per confirmed enquiry. Shopify only adds contact_posted after a successful send (spam checks
  // included), and the stored entry (with the email and phone) is removed straight away, so a refresh never counts
  // it twice and nothing personal stays in the browser.
  if (params.get('contact_posted') === 'true') {
    let lead = null;
    try {
      lead = JSON.parse(sessionStorage.getItem(LEAD_KEY) || 'null');
      sessionStorage.removeItem(LEAD_KEY);
    } catch {}
    if (lead) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'generate_lead', lead_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...lead });
    }
  }
})();

/* ------------------------------------------------------------------ plinth photos */
// Product photos on the plinth tile come in three kinds, told apart from the corners of a tiny copy of the image
// (the CDN allows cross-origin reads):
// - cut-outs on white: left alone, their white melts into the tile (mix-blend-multiply in app.css)
// - studio shots on a flat grey or off-white backdrop: the image paints its own backdrop colour across the tile, so
//   there is no visible box around the product
// - lifestyle photos (corners differ): fill the tile edge to edge
function checkPlinth(img) {
  if (!(img instanceof HTMLImageElement) || img.dataset.plinthDone !== undefined || !img.closest('.plinth')) return;
  if (!img.complete || !img.naturalWidth) return;
  try {
    const n = 20;
    const c = document.createElement('canvas');
    c.width = c.height = n;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, n, n);
    const d = ctx.getImageData(0, 0, n, n).data;
    const corners = [0, n - 1, n * (n - 1), n * n - 1].map((p) => [d[p * 4], d[p * 4 + 1], d[p * 4 + 2], d[p * 4 + 3]]);
    const white = corners.filter(([r, g, b, a]) => r > 245 && g > 245 && b > 245 && a > 200).length;
    const clear = corners.filter((k) => k[3] < 200).length;
    if (white < 3 && clear < 2) {
      const spread = [0, 1, 2].map((ch) => Math.max(...corners.map((k) => k[ch])) - Math.min(...corners.map((k) => k[ch])));
      const avg = [0, 1, 2].map((ch) => Math.round(corners.reduce((sum, k) => sum + k[ch], 0) / 4));
      const flat = Math.max(...spread) < 14;
      if (flat) {
        img.classList.add('plinth-flat');
        img.style.backgroundColor = `rgb(${avg.join(' ')})`;
      } else {
        img.classList.add('plinth-photo');
        if (!img.classList.contains('card-img-2')) img.closest('.plinth').classList.add('is-photo');
      }
    }
  } catch {}
  img.dataset.plinthDone = '';
}
document.documentElement.classList.add('plinth-ready');
document.querySelectorAll('.plinth img[crossorigin]').forEach(checkPlinth);
document.addEventListener('load', (e) => checkPlinth(e.target), true);
document.addEventListener('error', (e) => e.target instanceof HTMLImageElement && (e.target.dataset.plinthDone = ''), true);

/* ------------------------------------------------------------------ reveal on scroll */
if ('IntersectionObserver' in window && !reduceMotion.matches) {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-inview', '');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px' },
  );
  document.querySelectorAll('[data-reveal]').forEach((el, i) => {
    el.style.transitionDelay = `${(i % 4) * 70}ms`;
    io.observe(el);
  });
} else {
  document.querySelectorAll('[data-reveal]').forEach((el) => el.setAttribute('data-inview', ''));
}

/* ------------------------------------------------------------------ tickers */
// Moving word tickers (home clearance) run only while on screen; CSS keeps them still otherwise and for reduced motion.
if ('IntersectionObserver' in window) {
  const tickerIo = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.toggleAttribute('data-running', entry.isIntersecting));
  });
  document.querySelectorAll('[data-ticker]').forEach((el) => tickerIo.observe(el));
}
